/**
 * NominationDetail — one nomination, every field the service returns, for whoever opened it.
 *
 * Opened from the Nomination ID on the Nomination List and the Attendance List, and from a
 * shared link: the open nomination lives in the list page's URL (`?nominationId=`), so a
 * marketer can send a BD a link that lands on the list with this modal already open. That is
 * also why the review outcome leads the modal rather than sitting among the fields — for the
 * BD who follows such a link, "was it approved, and if not, why" is the whole question.
 *
 * Not in the visible Figma (an earlier version was built from a hidden layer, `245:28201`, and
 * removed for that reason); brought back on request, showing the complete nomination rather
 * than the hidden layer's subset.
 *
 * Read-only itself. The footer offers Edit — to the nominating BD, while the event is open
 * (utils/nominationEdit `canEditNomination`, decided by the caller, which has the event and the
 * login) — which opens the edit page; editing sends the nomination back to review. Deciding
 * happens from the row, which keeps one Reject dialog rather than one per surface.
 *
 * Sections, in the order someone reading a nomination needs them:
 *
 *   status banner         the outcome; the reason when rejected; who decided and when
 *   Nomination            id, event, submitted on
 *   Account Information   accountInformation.*
 *   Guest Information     guestInformation.*
 *   行程安排               formResponses — this event's own questions and the BD's answers
 *   BD Information        who submitted it (server-derived from the caller)
 *
 * SFDC Account and Company Revenue Size are on the submit form but not shown here: the model
 * has no field for either, so there is nothing to read back.
 */

import Alert from '@amzn/awsui-components-react/polaris/alert';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { useNavigate } from 'react-router-dom';
import type { Nomination } from '../../client/EventCalendarClient';
import {
  NOMINATION_STATUS_INDICATOR,
  NOMINATION_STATUS_LABELS,
} from '../../utils/nominationStatus';
import { formatDateTime } from '../../utils/eventDates';
import { canonicalJobTitle } from '../../utils/jobTitles';
import { guestNameOf } from '../../utils/guestName';

const DASH = '—';

/**
 * One labelled read-only value.
 *
 * A dash for anything empty rather than a blank space: several of these are optional or
 * server-derived and legitimately absent (`bdCellPhone` and `bdBusinessUnit` have no source
 * wired yet), and an empty gap reads as a rendering fault rather than as "not recorded".
 */
function Field({ label, children }: { label: string; children?: React.ReactNode }) {
  const empty = children === undefined || children === null || children === '';
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{empty ? <Box color="text-status-inactive">{DASH}</Box> : children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="h3" padding={{ bottom: 'xs' }}>
        {title}
      </Box>
      {children}
    </div>
  );
}

/**
 * The review outcome, as a banner.
 *
 * Who decided and when come from `decidedBy` / `decidedAt`, which the service sets once, in
 * the same write as the status change. Not `updatedBy` / `updatedAt`: those are the last write
 * of any kind, and stop meaning "the decision" as soon as a nomination can be changed after
 * it (attendance updates, for one).
 *
 * A nomination decided before those fields existed has neither, and then the banner names no
 * reviewer rather than guessing one from the last update.
 */
function StatusBanner({ nomination }: { nomination: Nomination }) {
  const { status } = nomination;
  if (!status) return null;
  const decided = status !== 'PENDING_REVIEW';
  const by = decided && nomination.decidedBy ? nomination.decidedBy : undefined;
  const at = decided && nomination.decidedAt ? formatDateTime(nomination.decidedAt) : undefined;
  const reviewed = by || at ? `Reviewed${by ? ` by ${by}` : ''}${at ? ` on ${at}` : ''}.` : null;

  if (status === 'APPROVED') {
    return (
      <Alert type="success" header="Approved">
        {reviewed}
      </Alert>
    );
  }
  if (status === 'REJECTED') {
    return (
      <Alert type="error" header="Rejected">
        <SpaceBetween size="xxs">
          <div>
            <Box variant="awsui-key-label">Rejection Reason</Box>
            {/* The reason is optional on RejectNomination; say so rather than show nothing. */}
            <div>{nomination.rejectReason || 'No reason was given.'}</div>
          </div>
          {reviewed ? <div>{reviewed}</div> : null}
        </SpaceBetween>
      </Alert>
    );
  }
  return (
    <Alert type="info" header="Pending Review">
      This nomination is waiting for a marketer to review it.
    </Alert>
  );
}

export interface NominationDetailProps {
  /**
   * The nomination to show; `undefined` while the list is still loading, or when the id in the
   * URL matches nothing this caller can see.
   */
  nomination: Nomination | undefined;
  /** True while the nominations are still being fetched. */
  loading?: boolean;
  /** Why the nominations could not be fetched, or null — kept apart from "not found". */
  error?: string | null;
  /** The event's name, resolved by the caller — a nomination carries only `eventId`. */
  eventName: string;
  onDismiss: () => void;
  /**
   * The edit page's link, when the reader may edit this nomination now; absent hides Edit. The
   * caller decides (canEditNomination), since it holds the event and the login.
   */
  editHref?: string;
}

export default function NominationDetail({
  nomination,
  loading,
  error,
  eventName,
  onDismiss,
  editHref,
}: NominationDetailProps) {
  const navigate = useNavigate();
  return (
    <Modal
      visible
      size="large"
      header="Nomination Details"
      onDismiss={onDismiss}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {nomination && editHref ? (
              <Button onClick={() => navigate(editHref)}>Edit</Button>
            ) : null}
            <Button variant="primary" onClick={onDismiss}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {nomination ? (
        <Body nomination={nomination} eventName={eventName} />
      ) : loading ? (
        <Box textAlign="center" padding="l">
          <Spinner /> Loading nomination…
        </Box>
      ) : error ? (
        <Alert type="error" header="Could not load the nomination">
          {error}
        </Alert>
      ) : (
        /* A shared link can name a nomination this caller cannot see: ListNominations gives a BD
           only their own and their reports', and the id could simply be wrong. The service does
           not say which, so neither can this. */
        <Alert type="warning" header="Nomination not found">
          This nomination does not exist, or you do not have access to it.
        </Alert>
      )}
    </Modal>
  );
}

function Body({ nomination, eventName }: { nomination: Nomination; eventName: string }) {
  const { accountInformation: account, guestInformation: guest } = nomination;
  const answers = nomination.formResponses ?? [];

  return (
    <SpaceBetween size="l">
      <StatusBanner nomination={nomination} />

      <Section title="Nomination">
        <ColumnLayout columns={2} variant="text-grid">
          <Field label="Nomination ID">{nomination.nominationId}</Field>
          <Field label="Event">{eventName}</Field>
          <Field label="Nomination Status">
            {nomination.status ? (
              <StatusIndicator type={NOMINATION_STATUS_INDICATOR[nomination.status]}>
                {NOMINATION_STATUS_LABELS[nomination.status]}
              </StatusIndicator>
            ) : undefined}
          </Field>
          <Field label="Submitted On">
            {nomination.createdAt ? formatDateTime(nomination.createdAt) : undefined}
          </Field>
        </ColumnLayout>
      </Section>

      <Section title="Account Information">
        <ColumnLayout columns={2} variant="text-grid">
          <Field label="Company Name">{account?.companyName}</Field>
          {/* Passed through from the contact's SFDC account; unknown tags shown as given. Older
              nominations predate the field and have none. */}
          <Field label="Account Tags">
            {account?.accountTags?.length ? account.accountTags.join(', ') : undefined}
          </Field>
        </ColumnLayout>
      </Section>

      <Section title="Guest Information">
        <ColumnLayout columns={2} variant="text-grid">
          <Field label="Guest Name">{guestNameOf(guest)}</Field>
          <Field label="Job Title">{canonicalJobTitle(guest?.jobTitle)}</Field>
          <Field label="Phone Number">{guest?.phoneNumber}</Field>
          <Field label="Email">{guest?.email}</Field>
          <Field label="Customer BU">{guest?.businessUnit}</Field>
        </ColumnLayout>
      </Section>

      <Section title="行程安排">
        {/* Whatever the marketer configured on this event, with the BD's answers. The questions
            are copied onto the nomination at submit time, so this stays readable even after the
            event's form changes — which is also why the question text comes from here rather
            than from the event. */}
        {answers.length === 0 ? (
          <Box color="text-status-inactive">This event asked no additional questions.</Box>
        ) : (
          <ColumnLayout columns={2} variant="text-grid">
            {answers.map((response, index) => (
              /* Keyed by position: `formResponses` is positional and the question text is not
                 unique — a marketer can ask the same thing twice. */
              <Field key={index} label={response.question || `Question ${index + 1}`}>
                {/* An unanswered question is stored as "", and every question is optional. */}
                {response.answer || undefined}
              </Field>
            ))}
          </ColumnLayout>
        )}
      </Section>

      <Section title="BD Information">
        <ColumnLayout columns={2} variant="text-grid">
          <Field label="BD Name">{nomination.bdName}</Field>
          <Field label="BD Login">{nomination.createdBy}</Field>
          <Field label="BD BU">{nomination.bdBusinessUnit}</Field>
          <Field label="BD Phone">{nomination.bdCellPhone}</Field>
        </ColumnLayout>
      </Section>
    </SpaceBetween>
  );
}
